/**
 * The responsibility of this component is to manage event descriptions, terms and conditions, and FAQs.
 *
 * Flow:
 * 1. Renders a form with multiple editable sections
 * 2. Allows toggling between view and edit mode
 * 3. Handles form submission that advances to the next tab
 * 4. Provides back navigation to the Schedule tab
 *
 * @component
 * @returns {JSX.Element} The rendered EventDescription form component
 */

import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Autocomplete, TextField, Button, InputAdornment, CircularProgress } from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import Tooltips from '../../../../../../../../components/FormFields/Tooltips';
import useEventDescription from '../../../hooks/useEventDescription';
import { previousTab } from '../../../../../../../../app/store/slices/event/event';
import { H4 } from '../../../../../../../../themes/typography';
import '../../../../../../../../styles/components/Description.css';

export default function EventDescription() {
    const dispatch = useDispatch();
    const { tags } = useSelector((state) => state.eventSetup);

    const {
        loading,
        formik,
        isEditModeOn,
        setIsEditModeOn,
        toolbarOptions,
        getOptionLabel,
        handleTagsChange,
        handleFilterOptions,
        handleMobileNumberChange,
        shouldShowEditButton
    } = useEventDescription();

    return (
        <form noValidate id="description-form" onSubmit={formik.handleSubmit}>
            <div className="event-description-terms-faqs-main-container">
                {shouldShowEditButton && (
                    <div style={editBtnStyle}>
                        <Button
                            startIcon={<BorderColorIcon />}
                            variant="text"
                            onClick={() => setIsEditModeOn(true)}
                            disabled={isEditModeOn}
                        >
                            Edit
                        </Button>
                    </div>
                )}

                <div className="event-description-form-starts">
                    <DescriptionBox toolTip={<Tooltips />} titleName="Event Description *">
                        <ReactQuill
                            theme="snow"
                            key="event-description"
                            readOnly={!isEditModeOn}
                            value={formik.values.description}
                            modules={{ toolbar: toolbarOptions }}
                            placeholder="Describe your event here..."
                            onChange={(value) => formik.setFieldValue('description', value)}
                        />
                    </DescriptionBox>

                    <TextField
                        inputMode="numeric"
                        id="creator-mobile-number"
                        name="creatorMobileNumber"
                        label="Event Creator Mobile Number"
                        disabled={!isEditModeOn}
                        onBlur={formik.handleBlur}
                        onChange={handleMobileNumberChange}
                        value={formik.values.creatorMobileNumber}
                        error={
                            formik.touched.creatorMobileNumber &&
                            Boolean(formik.errors.creatorMobileNumber)
                        }
                        helperText={
                            formik.touched.creatorMobileNumber && formik.errors.creatorMobileNumber
                        }
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">+91</InputAdornment>
                                ),
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                onInput: (e) => {
                                    e.target.value = e.target.value.replace(/\D/g, '');
                                    if (e.target.value.length > 10) {
                                        e.target.value = e.target.value.slice(0, 10);
                                    }
                                }
                            }
                        }}
                    />

                    <Autocomplete
                        id="event-tags"
                        freeSolo
                        multiple
                        fullWidth
                        clearOnBlur
                        selectOnFocus
                        handleHomeEndKeys
                        disableCloseOnSelect
                        options={tags}
                        disabled={!isEditModeOn}
                        slotProps={slotProps}
                        value={formik.values.tags}
                        onChange={handleTagsChange}
                        getOptionLabel={getOptionLabel}
                        filterOptions={handleFilterOptions}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps}>
                                    {option.name}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="event-tags"
                                label="Event Tags"
                                placeholder={
                                    formik.values.tags.length === 0 ? 'Select or add tags' : ''
                                }
                            />
                        )}
                    />

                    <DescriptionBox toolTip={<Tooltips />} titleName="Terms & Conditions">
                        <ReactQuill
                            theme="snow"
                            readOnly={!isEditModeOn}
                            key="terms-and-conditions"
                            value={formik.values.termsAndCondition}
                            modules={{ toolbar: toolbarOptions }}
                            placeholder="Describe your terms and conditions here..."
                            onChange={(value) => formik.setFieldValue('termsAndCondition', value)}
                        />
                    </DescriptionBox>

                    <DescriptionBox titleName="FAQ's" toolTip={<Tooltips />}>
                        <ReactQuill
                            theme="snow"
                            key="event-faqs"
                            readOnly={!isEditModeOn}
                            value={formik.values.faqs}
                            modules={{ toolbar: toolbarOptions }}
                            onChange={(value) => formik.setFieldValue('faqs', value)}
                            placeholder="Describe your frequently asked questions here..."
                        />
                    </DescriptionBox>
                </div>
            </div>

            <div className="submit-button-container">
                <Button variant="text" onClick={() => dispatch(previousTab())}>
                    Back to Schedule
                </Button>
                <Button variant="main" disabled={loading} type="submit">
                    {/* {isEditModeOn ? 'Save' : 'Next'} */}
                    Next{' '}
                    {loading && (
                        <CircularProgress
                            size={20}
                            sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                        />
                    )}
                </Button>
            </div>
        </form>
    );
}

function DescriptionBox({ titleName, toolTip, children, helperText }) {
    return (
        <div className="description-box-container">
            <div className="description-box-wrapper">
                <div className="title-tooltip-edit-container">
                    <div className="title-tooltip-container">
                        <H4>{titleName}</H4>
                        {toolTip}
                    </div>
                </div>
                <div className="quill-rich-text-editor">
                    {children}
                    {helperText}
                </div>
            </div>
        </div>
    );
}

const slotProps = {
    popper: {
        placement: 'bottom',
        disablePortal: true,
        modifiers: [
            {
                name: 'flip',
                enabled: false
            }
        ]
    }
};

const editBtnStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    paddingBottom: '0.5rem'
};
